import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, User, Award, Loader2, AlertCircle, X } from "lucide-react";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { SpotIncentive } from "@/bryntum-lib/models/SpotIncentive";
import { toast } from "sonner";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys";
import { Resource } from "@/bryntum-lib/models/Resource";

interface PointChange {
  id: string;
  change: number;
  description: string;
  isReleased: boolean;
  incentiveId: number;
  resources: Resource[];
}

export const IncentivePointsSection = () => {
  const { file, shouldShowSpotIncentiveButton } = useFileDetailModal();
  const { project } = useSwivuBryntumContext();
  const [isReleasing, setIsReleasing] = useState(false);
  const [archivingRecordId, setArchivingRecordId] = useState<number | null>(null);

  // Subscribe to store changes for reactive updates
  const { subscribe: subscribeToSpotIncentive, version } = useSwivuStore<SpotIncentive>({ storeId: STORE_KEYS.spotIncentiveStore });

  // Check if user has permission to archive
  const canArchive = useMemo(() => {
    return checkIncentivePermission({
      project,
      permissionKey: INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS
    });
  }, [project]);

  // Get spot incentives for the current file (excluding archived)
  const spotIncentives = useMemo(() => {
    if (!project || !file?.id) return [];
    const store = project.spotIncentiveStore;
    if (!store) return [];
    return store.query((incentive: SpotIncentive) =>
      incentive.fileId === file.id && !incentive.archived
    ) as SpotIncentive[];
  }, [project, file?.id, version]);

  // Check if there are any unreleased spot incentives for this file
  const unreleasedIncentivesCount = useMemo(() => {
    if (!project || !file?.id) return 0;
    const store = project.spotIncentiveStore;
    if (!store) return 0;
    const unreleasedIncentives = store.query((incentive: SpotIncentive) =>
      incentive.fileId === file.id && !incentive.isReleased && !incentive.archived
    ) as SpotIncentive[];
    return unreleasedIncentives.length;
  }, [project, file?.id, version]);

  const hasUnreleasedIncentives = unreleasedIncentivesCount > 0;

  // Map spot incentives to PointChange interface
  const pointChanges = useMemo<PointChange[]>(() => {
    if (!project || !spotIncentives.length) return [];

    const changes = spotIncentives.map((incentive: SpotIncentive) => {
      const resources = (incentive.resources as Resource[]) || [];

      // Calculate total points from positive tags
      const positivePoints = (incentive.positiveTagIds || []).reduce((sum: number, tagId: number) => {
        const tag = project.incentiveTagsStore?.getById(tagId);
        const points = tag?.categoryRecord?.points || 0;
        return sum + points;
      }, 0);

      // Calculate total points from negative tags
      const negativePoints = (incentive.negativeTagIds || []).reduce((sum: number, tagId: number) => {
        const tag = project.incentiveTagsStore?.getById(tagId);
        const points = tag?.categoryRecord?.points || 0;
        return sum + points;
      }, 0);

      // Calculate net change for this incentive
      const netChange = positivePoints - negativePoints;

      return {
        id: `incentive-${incentive.id}`,
        change: netChange,
        description: incentive.name || "",
        isReleased: incentive.isReleased,
        incentiveId: incentive.id,
        resources,
      };
    });

    // Sort: pending (not released) records first, then released records
    return changes.sort((a, b) => {
      if (a.isReleased === b.isReleased) return 0;
      return a.isReleased ? 1 : -1;
    });
  }, [spotIncentives, project]);

  // Calculate totals
  const netChange = useMemo(() => {
    return pointChanges.reduce((sum, change) => sum + change.change, 0);
  }, [pointChanges]);


  useEffect(() => {
    return subscribeToSpotIncentive(({ record: changedRecord }) => {
      return changedRecord.fileId === file?.id;
    });
  }, [subscribeToSpotIncentive, file]);

  const handleArchiveIncentive = async (incentiveId: number) => {
    if (!project?.spotIncentiveStore || !canArchive) return;

    setArchivingRecordId(incentiveId);
    try {
      const incentive = project.spotIncentiveStore.getById(incentiveId);
      if (incentive) {
        incentive.archived = true;
        await project.sync();
        toast.success("Point record removed");
      }
    } catch (error) {
      console.error("Failed to archive point:", error);
      toast.error("Failed to remove point record");
    } finally {
      setArchivingRecordId(null);
    }
  };

  // Starting points would need to come from a different source (e.g., task or resource baseline)
  // For now, we'll show net change and final points as net change
  const startingPoints = 0; // This would need to be calculated from baseline points
  const finalPoints = startingPoints + netChange;
  const hasNoPoints = finalPoints === 0 && pointChanges.length === 0;

  // Show empty state when there are no points
  if (hasNoPoints) {
    return (
      <div className="h-full w-full flex items-center justify-center overflow-hidden p-6">
        <div className="flex flex-col items-center gap-6 rounded-xl border border-dashed border-border bg-muted/50 px-8 py-10 text-center max-w-md w-full mx-auto">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-4 ring-primary/5">
            <Award className="size-7" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">No points assigned yet</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              Award or deduct points for resources.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Scrollable Content Area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
        {/* Unreleased Incentives Notice */}
        {hasUnreleasedIncentives && (
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/50 px-4 py-3">
            <AlertCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {unreleasedIncentivesCount} {unreleasedIncentivesCount === 1 ? 'record needs' : 'records need'} to be released
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Click "Release" below to release {unreleasedIncentivesCount === 1 ? 'this point' : 'these points'}
              </p>
            </div>
          </div>
        )}

        {/* Transaction List */}
        {pointChanges.length > 0 && (
          <div className="space-y-2.5">
            {pointChanges.map((change) => (
              <div
                key={change.id}
                className="group flex items-start gap-3 p-3.5 rounded-lg border border-border bg-muted/50 hover:bg-muted transition-all duration-200"
              >
                <div
                  className={`flex items-center justify-center h-8 w-8 rounded-full shrink-0 ${change.change > 0
                      ? "bg-green-600/10 dark:bg-green-500/15 text-green-700 dark:text-green-400"
                      : "bg-destructive/10 text-destructive"
                    }`}
                >
                  {change.change > 0 ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : (
                    <ArrowDown className="h-4 w-4" />
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span
                      className={`text-base font-bold ${change.change > 0
                          ? "text-green-700 dark:text-green-400"
                          : change.change < 0
                          ? "text-destructive"
                          : "text-muted-foreground"
                        }`}
                    >
                      {change.change > 0 ? `+${change.change}` : change.change}
                    </span>
                    <span className="text-sm font-medium text-foreground">{change.description}</span>
                    {change.isReleased ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-600/10 dark:bg-green-500/15 text-green-700 dark:text-green-400 border border-green-600/20 dark:border-green-500/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-600 dark:bg-green-500"></span>
                        Released
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                    <User className="h-3 w-3 shrink-0" />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(() => {
                        const allUserNames = change.resources.flatMap((resource) =>
                          resource.eligibleUserValues || []
                        );
                        const uniqueUserNames = Array.from(new Set(allUserNames));

                        return uniqueUserNames.length > 0 ? (
                          uniqueUserNames.map((userName, index) => (
                            <span key={`${userName}-${index}`}>
                              {userName}{index < uniqueUserNames.length - 1 ? ',' : ''}
                            </span>
                          ))
                        ) : (
                          <span className="text-muted-foreground/60">No resources assigned</span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                {canArchive && !change.isReleased && (
                  <button
                    onClick={() => handleArchiveIncentive(change.incentiveId)}
                    disabled={archivingRecordId === change.incentiveId}
                    className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center h-7 w-7 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                    title="Remove point record"
                  >
                    {archivingRecordId === change.incentiveId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      {shouldShowSpotIncentiveButton && hasUnreleasedIncentives && (
        <div className="flex-shrink-0 bg-card">
          <div className="px-6 pt-3 pb-4 border-t border-border">
            <div className="flex items-center justify-end">
              <Button
                onClick={async (e) => {
                  e.stopPropagation();
                  setIsReleasing(true);
                  try {
                    // Update all unreleased spot incentives for this file to RELEASED
                    if (project?.spotIncentiveStore && file?.id) {
                      const unreleasedIncentives = project.spotIncentiveStore.query((incentive: SpotIncentive) =>
                        incentive.fileId === file.id && !incentive.isReleased
                      ) as SpotIncentive[];

                      if (unreleasedIncentives.length > 0) {
                        unreleasedIncentives.forEach((incentive) => {
                          incentive.isReleased = true;
                        });
                        await project.sync();
                        toast.success("Points adjusted are now visible on the Points surface.");
                      }
                    }
                  } finally {
                    setIsReleasing(false);
                  }
                }}
                disabled={isReleasing}
                className="inline-flex items-center gap-2"
                variant="default"
                size="sm"
              >
                {isReleasing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Releasing points...
                  </>
                ) : (
                  "Release"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
